import type {
  MembershipRoleChoiceResponse,
  OrganizationRoleResponse,
  RelationshipStatus,
} from "@kontextwerk/control-web-client"
import {
  isIdentityChangeConflict,
  type MembershipAccess,
  type MembershipAccessApi,
} from "../../../../../../../api/kehrblatt/membership-access"

export type MemberAccessPort = Pick<MembershipAccessApi, "load" | "change" | "roleChoices" | "role">

/** Presentation projection of the confirmed grants; every read and write is re-checked server-side. */
export type MemberAccessCapabilities = Readonly<{
  /** core.memberships view: the directory record of a member. */
  viewMembership: boolean
  /** core.memberships manage: assignable roles and access changes. */
  manageMembership: boolean
  /** core.roles view: role names, kinds and grants. */
  viewRoles: boolean
}>

/** The confirmed context's own relationship; it stays read-only and needs no directory read. */
export type OwnMembership = Readonly<{
  membershipId: string
  userId: string
  organizationId: string
  roleId: string
  roleName: string
  revision: number
}>

export type MemberAccessOptions = Readonly<{
  membershipId: string
  port: MemberAccessPort
  capabilities: MemberAccessCapabilities
  own: OwnMembership | null
  /** Role of the acting membership; only an owner changes another owner's status. */
  actorRoleId: string | null
  delegated: boolean
  /** Lifetime of the captured context; its end discards every loaded fact and draft. */
  signal: AbortSignal
}>

export type MemberAccessConfirm = <T>(action: () => Promise<T>) => Promise<T | undefined>

export type MemberAccessSaveResult =
  | "saved"
  | "unchanged"
  | "cancelled"
  | "conflict"
  | "failed"
  | "obsolete"

export type MemberRoleState = Readonly<{
  status: "loading" | "ready" | "error"
  role: OrganizationRoleResponse | null
}>

export type MemberRoleOption = Readonly<{ value: string; name: string }>

export const OWNER_ROLE_LABEL = "Inhaber"

const ownRecord = (own: OwnMembership): MembershipAccess =>
  Object.freeze({
    id: own.membershipId.toLowerCase(),
    user_id: own.userId.toLowerCase(),
    organization_id: own.organizationId.toLowerCase(),
    role_id: own.roleId.toLowerCase(),
    // The current organization admission proves the own relationship is active.
    status: "active",
    revision: own.revision,
    email: null,
  })

/**
 * Role and status of one membership plus its local draft. The subview owns it for one confirmed
 * member identity, so a tab change keeps the draft while a context change discards everything.
 */
export class MemberAccess {
  record = $state<MembershipAccess | null>(null)
  status = $state<"loading" | "ready" | "error" | "unavailable" | "invalidated">("loading")
  choices = $state<readonly MembershipRoleChoiceResponse[]>([])
  roles = $state<Readonly<Record<string, MemberRoleState>>>({})
  draftRoleId = $state<string | null>(null)
  draftStatus = $state<RelationshipStatus | null>(null)
  saving = $state(false)

  readonly membershipId: string
  /** The actor's own membership: role and status stay with other administrators. */
  readonly self: boolean
  private readonly options: MemberAccessOptions
  private readonly lifetime = new AbortController()
  private readonly endContext = () => this.invalidate()
  private loadTicket = 0

  constructor(options: MemberAccessOptions) {
    this.options = options
    this.membershipId = options.membershipId.toLowerCase()
    this.self = options.own?.membershipId.toLowerCase() === this.membershipId

    if (options.signal.aborted) this.invalidate()
    else options.signal.addEventListener("abort", this.endContext, { once: true })
  }

  // Derived facts are computed from the reactive fields on every read; nothing is kept in sync.
  get currentRole(): OrganizationRoleResponse | null {
    return this.record ? (this.roles[this.record.role_id]?.role ?? null) : null
  }

  get isOwnerMembership(): boolean {
    return this.currentRole?.kind === "owner"
  }

  get roleAssignable(): boolean {
    const record = this.record

    return record ? this.choices.some((choice) => choice.id === record.role_id) : false
  }

  get roleEditable(): boolean {
    return this.writable && this.roleAssignable && !this.isOwnerMembership
  }

  get statusEditable(): boolean {
    return (
      this.writable &&
      (this.roleAssignable ||
        (this.isOwnerMembership &&
          !this.options.delegated &&
          this.options.actorRoleId?.toLowerCase() === this.record?.role_id))
    )
  }

  get editable(): boolean {
    return this.roleEditable || this.statusEditable
  }

  get dirty(): boolean {
    const record = this.record

    return Boolean(
      record && (this.draftRoleId !== record.role_id || this.draftStatus !== record.status),
    )
  }

  get roleOptions(): MemberRoleOption[] {
    const record = this.record

    if (!record) return []

    if (this.isOwnerMembership) return [{ value: record.role_id, name: OWNER_ROLE_LABEL }]

    const options = this.choices.map((choice) => ({ value: choice.id, name: choice.name }))

    if (!options.some((option) => option.value === record.role_id))
      options.unshift({
        value: record.role_id,
        name: this.roleName(record.role_id) ?? "Aktuelle Rolle",
      })

    return options
  }

  get currentRoleName(): string | null {
    return this.record ? this.roleName(this.record.role_id) : null
  }

  get selectedRole(): MemberRoleState | null {
    return this.draftRoleId ? (this.roles[this.draftRoleId] ?? null) : null
  }

  get canViewRoles(): boolean {
    return this.options.capabilities.viewRoles
  }

  private get writable(): boolean {
    return (
      this.status === "ready" &&
      Boolean(this.record) &&
      this.options.capabilities.manageMembership &&
      !this.self
    )
  }

  roleName(roleId: string | null): string | null {
    if (!roleId) return null

    const role = this.roles[roleId]?.role

    if (role?.kind === "owner") return OWNER_ROLE_LABEL

    const own = this.options.own

    return (
      role?.name ??
      this.choices.find((choice) => choice.id === roleId)?.name ??
      (own && own.roleId.toLowerCase() === roleId ? own.roleName : null)
    )
  }

  async load(): Promise<void> {
    if (this.status === "invalidated") return

    const ticket = ++this.loadTicket
    const signal = this.lifetime.signal
    this.status = "loading"
    try {
      const record = await this.readRecord(signal)

      if (!this.owns(ticket)) return

      if (!record) {
        this.status = "unavailable"

        return
      }

      const [choices] = await Promise.all([
        this.options.capabilities.manageMembership && !this.self
          ? this.options.port.roleChoices(signal)
          : Promise.resolve([] as readonly MembershipRoleChoiceResponse[]),
        this.loadRole(record.role_id),
      ])

      if (!this.owns(ticket)) return

      this.choices = choices
      this.record = record
      this.resetDraft(record)
      this.status = "ready"
    } catch {
      if (this.owns(ticket)) this.status = "error"
    }
  }

  /** Reads a role's name and grants once; a failed read can be repeated explicitly. */
  async loadRole(roleId: string): Promise<void> {
    const signal = this.lifetime.signal
    const existing = this.roles[roleId]

    if (
      !this.options.capabilities.viewRoles ||
      signal.aborted ||
      (existing && existing.status !== "error")
    )
      return

    this.roles = { ...this.roles, [roleId]: { status: "loading", role: null } }
    try {
      const role = await this.options.port.role(roleId, signal)

      if (signal.aborted) return

      if (role.id !== roleId) throw new TypeError("Unexpected organization role")

      this.roles = { ...this.roles, [roleId]: { status: "ready", role } }
    } catch {
      if (!signal.aborted) this.roles = { ...this.roles, [roleId]: { status: "error", role: null } }
    }
  }

  selectRole(roleId: string) {
    if (
      !this.roleEditable ||
      this.saving ||
      !this.roleOptions.some((option) => option.value === roleId)
    )
      return

    this.draftRoleId = roleId
    void this.loadRole(roleId)
  }

  selectStatus(status: RelationshipStatus) {
    if (!this.statusEditable || this.saving) return

    if (status !== "active" && status !== "suspended") return

    this.draftStatus = status
  }

  discard() {
    if (this.saving || !this.record) return

    this.resetDraft(this.record)
  }

  /** Sends the draft once after the caller's confirmation; failures keep it without a replay. */
  async save(confirm: MemberAccessConfirm): Promise<MemberAccessSaveResult> {
    const base = this.record
    const roleId = this.draftRoleId
    const status = this.draftStatus

    if (!base || !roleId || !status || !this.dirty || !this.editable || this.saving)
      return "unchanged"

    const signal = this.lifetime.signal
    this.saving = true
    try {
      const outcome = await confirm(() =>
        this.options.port.change(
          this.membershipId,
          { role_id: roleId, status, expected_revision: base.revision },
          signal,
        ),
      )

      if (signal.aborted) return "obsolete"

      if (!outcome) return "cancelled"

      // The confirmed server state, changed or already equal, is the new comparison base.
      this.record = outcome.membership
      this.resetDraft(outcome.membership)
      void this.loadRole(outcome.membership.role_id)

      return "saved"
    } catch (error) {
      if (signal.aborted) return "obsolete"

      if (isIdentityChangeConflict(error)) {
        await this.rebase(base, signal)

        return signal.aborted ? "obsolete" : "conflict"
      }

      return (error as { name?: unknown } | null)?.name === "AbortError" ? "cancelled" : "failed"
    } finally {
      if (!signal.aborted) this.saving = false
    }
  }

  dispose() {
    this.options.signal.removeEventListener("abort", this.endContext)
    this.lifetime.abort()
  }

  private owns(ticket: number) {
    return ticket === this.loadTicket && !this.lifetime.signal.aborted
  }

  private async readRecord(signal: AbortSignal): Promise<MembershipAccess | null> {
    const own = this.self ? this.options.own : null

    if (!this.options.capabilities.viewMembership) return own ? ownRecord(own) : null

    try {
      return await this.options.port.load(this.membershipId, signal)
    } catch (error) {
      if (own && !signal.aborted) return ownRecord(own)

      throw error
    }
  }

  /** Reloads after a rejected change and keeps only the fields the user actually changed. */
  private async rebase(base: MembershipAccess, signal: AbortSignal) {
    const changedRole = this.draftRoleId !== base.role_id ? this.draftRoleId : null
    const changedStatus = this.draftStatus !== base.status ? this.draftStatus : null
    try {
      const fresh = await this.options.port.load(this.membershipId, signal)

      if (signal.aborted) return

      this.record = fresh
      this.resetDraft(fresh)
      await this.loadRole(fresh.role_id)

      if (signal.aborted) return

      if (changedRole && this.roleEditable) this.draftRoleId = changedRole

      if (changedStatus && this.statusEditable) this.draftStatus = changedStatus
    } catch {
      if (!signal.aborted) this.status = "error"
    }
  }

  private resetDraft(record: MembershipAccess) {
    this.draftRoleId = record.role_id
    this.draftStatus = record.status
  }

  private invalidate() {
    this.lifetime.abort()
    this.loadTicket += 1
    this.status = "invalidated"
    this.record = null
    this.choices = []
    this.roles = {}
    this.draftRoleId = null
    this.draftStatus = null
    this.saving = false
  }
}
